import { CASE_STUDIES } from "#pages/data";
import usePageContext from "#renderer/usePageContext";
import {
    Badge,
    Box,
    Button,
    Container,
    Divider,
    Flex,
    Heading,
    HStack,
    Icon,
    Image,
    Text,
    useColorModeValue
} from "@chakra-ui/react";
import React from "react";
import { FaArrowLeft } from "react-icons/fa/index.js";
import { ReactMarkdown } from "react-markdown/lib/react-markdown";

export const CaseStudyDetail: React.FC = () => {
    const pageContext = usePageContext();
    const slug = pageContext.routeParams?.slug;
    const study = CASE_STUDIES.find((t) => t.slug === slug);

    const bgMain = useColorModeValue("white", "gray.900");
    const bgSidebar = useColorModeValue("gray.50", "gray.800");
    const borderColor = useColorModeValue("gray.200", "gray.700");
    const textColor = useColorModeValue("gray.700", "gray.300");
    const blackOrWhiteColor = useColorModeValue('black', 'white');

    if (!study) {
        return (
            <Container maxW="container.xl" py={20} textAlign="center">
                <Heading>Case study not found</Heading>
                <Button as="a" href="/case-studies" mt={6} colorScheme="blue">
                    Return to Gallery
                </Button>
            </Container>
        );
    }

    return (
        <Box bg={bgMain} py={{ base: 12, md: 24 }}>
            <Container maxW="container.xl">
                <Button
                    as="a"
                    href="/case-studies"
                    variant="ghost"
                    leftIcon={<Icon as={FaArrowLeft} />}
                    mb={8}
                >
                    Back to all Case Studies
                </Button>

                <Flex
                    direction={{ base: "column", lg: "row" }}
                    gap={{ base: 10, lg: 16 }}
                    align="flex-start"
                >
                    {/* Main Content Area */}
                    <Box flex="1">
                        <Badge colorScheme="blue" mb={4} px={3} py={1} borderRadius="full">
                            Impact Study
                        </Badge>
                        <Heading as="h1" size="2xl" mb={6} lineHeight="shorter">
                            {study.title}
                        </Heading>

                        <HStack mb={8} spacing={4}>
                            <Box p={2} bg="white" borderRadius="md" border="1px solid" borderColor={borderColor}>
                                <Image
                                    src={study.imgSrc}
                                    alt={`${study.companyName} logo`}
                                    w="auto"
                                    h="30px"
                                    objectFit="contain"
                                    fallbackSrc="/img/clients/default.webp"
                                />
                            </Box>
                            <Box>
                                <Heading as="h2" size="sm">
                                    {study.companyName}
                                </Heading>
                                <Text fontSize="sm" color="gray.500">
                                    {study.companyDesc}
                                </Text>
                            </Box>
                        </HStack>

                        <Divider mb={8} borderColor={borderColor} />

                        <Box
                            className="markdown-body"
                            fontSize="lg"
                            color={textColor}
                            sx={{
                                '& h2': { fontSize: '2xl', fontWeight: 'bold', mt: 8, mb: 4, color: blackOrWhiteColor },
                                '& h3': { fontSize: 'xl', fontWeight: 'bold', mt: 6, mb: 3, color: blackOrWhiteColor },
                                '& p': { mb: 4, lineHeight: 1.8 },
                                '& ul': { mb: 6, pl: 6 },
                                '& li': { mb: 2 }
                            }}
                        >
                            <ReactMarkdown
                                components={{
                                    li: ({ ...props }) => {
                                        props["ordered"] = "false" as unknown as boolean;
                                        return (
                                            <li
                                                {...props}
                                                style={{
                                                    marginLeft: "1rem",
                                                    paddingLeft: "0.5rem",
                                                    marginBottom: "0.5rem",
                                                }}
                                            />
                                        );
                                    },
                                }}
                            >
                                {study.summary}
                            </ReactMarkdown>
                        </Box>
                    </Box>

                    {/* Sticky Sidebar */}
                    <Box
                        w={{ base: "full", lg: "400px" }}
                        position={{ lg: "sticky" }}
                        top={{ lg: "120px" }}
                    >
                        <Box
                            bg={bgSidebar}
                            p={8}
                            borderRadius="2xl"
                            border="1px solid"
                            borderColor={borderColor}
                            boxShadow="sm"
                        >
                            <Heading as="h3" size="md" mb={6}>
                                Need Similar Results?
                            </Heading>
                            <Text fontSize="sm" color={textColor} mb={6}>
                                At TenK Solutions, we engineer growth by securing unbiased quotes from over 200 global providers and executing frictionless IT migrations.
                            </Text>
                            <Button as="a" href="#Contact" colorScheme="blue" size="lg" w="full">
                                Get a Free Assessment
                            </Button>
                        </Box>
                    </Box>
                </Flex>
            </Container>
        </Box>
    );
};
